import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  effectiveSearchQuery,
  err,
  searchWireParams,
  type FeedItem,
  type ParsedList,
  type Result,
  type SearchQuery,
} from '@visious/core';
import { fetchSearch } from '../../api/search';
import type { ApiProblem } from '../../api/videos';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { systemClock } from '../../platform/clock';
import { useSettings } from '../../platform/settings';
import { getPlatform } from '../../platform/instance';
import { searchControls, searchStateFor, type SearchControls, type SearchState } from './model';

/**
 * The React binding for §8's Search page. Every decision is in `model.ts`; what is here is the query,
 * the clock and the region read — the three things that are not pure.
 *
 * **`useInfiniteQuery` since auto-load arrived**, the subscriptions feed's shape: one list, one
 * identity, append-only, and the cursor is a number the page count determines. The route's own `page`
 * is where the list *starts* — a deep link to `?page=3` begins there and appends 4, 5, … — and the
 * URL is deliberately not rewritten as pages accumulate: the accumulated list is not addressable, and
 * pretending page 5 alone reproduces it would be a lying link.
 *
 * `retry: false` for the reason 1c established: §3's retry and backoff live
 * in `createApiTransport`, so a second retry loop here would multiply requests against an instance that
 * is already failing.
 */
export interface SearchBinding {
  readonly query: SearchQuery;
  readonly controls: SearchControls;
  readonly state: SearchState;
  readonly retry: () => void;
  readonly refreshing: boolean;
  readonly loadMore: () => void;
  readonly loadingMore: boolean;
}

/** A page that failed is a value here, not a throw — the transport returns its errors (1c). */
type SearchResult = Result<ParsedList<FeedItem>, ApiProblem>;

async function loadSearch(query: SearchQuery, region: string | null): Promise<SearchResult> {
  const boot = await getPlatform();
  if (!boot.ok) return err({ kind: 'bad-url', path: '/api/v1/search' });
  return await fetchSearch(boot.platform, query, region);
}

export function useSearch(query: SearchQuery): SearchBinding {
  // Through the store so a region change on the settings page reaches an open results page (3e).
  const region = useSettings().contentRegion;

  // Keyed on the **wire** parameters rather than on the query object, minus the page — the page is the
  // cursor now, not part of the list's identity. Two consequences of the wire key, both wanted:
  // `features: ['hd','hdr']` and `['hdr','hd']` are one cache entry (the normaliser canonicalises the
  // order), and a query whose filters are suppressed by an operator shares its entry with the same
  // query at the defaults — because it is literally the same request.
  const wireWithPage = searchWireParams(effectiveSearchQuery(query), { region });
  const wire = { ...wireWithPage, page: null };

  const request = useInfiniteQuery({
    // The starting page is part of the identity: a list that began at page 3 is not the page-1 list
    // with pages missing, and sharing a cache entry would splice them.
    queryKey: ['search', wire, query.page],
    initialPageParam: query.page,
    queryFn: ({ pageParam }) => loadSearch({ ...query, page: pageParam }, region),
    getNextPageParam: (_last, all) => query.page + all.length,
    retry: false,
    // A search is a snapshot of a ranking, and the ranking is not even stable between two identical
    // requests seconds apart (`search/query.ts`). So re-fetching on a back-navigation would change the
    // page under the reader for no reason; five minutes is long enough that it does not.
    staleTime: 5 * 60 * 1000,
  });

  const progress = useWatchProgressLookup();

  const state = useMemo(() => {
    if (request.isPending) return searchStateFor(query, null, { nowEpochSec: 0, progress });

    const results: readonly SearchResult[] =
      request.error !== null
        ? // A thrown query is a bug or an abort rather than a classified failure — the transport returns
          // its errors as values. Reported as a network problem, the honest reading of "no answer".
          [err({ kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } })]
        : (request.data?.pages ?? []);

    // Relative dates are anchored to when the rows *arrived*, not to this render — a page left open
    // would otherwise creep from "2 hours ago" to "3 hours ago" while the list itself is stale.
    const arrivedAt = request.dataUpdatedAt > 0 ? request.dataUpdatedAt : systemClock.nowEpochMs();

    return searchStateFor(query, results, { nowEpochSec: Math.floor(arrivedAt / 1000), progress });
  }, [progress, query, request.data, request.dataUpdatedAt, request.error, request.isPending]);

  return {
    query,
    // A function of the query alone, so the filter bar renders while the request is in flight and
    // survives its failure — which is the whole point: the reader has to be able to change the filter
    // that failed.
    controls: useMemo(() => searchControls(query), [query]),
    state,
    retry: () => void request.refetch(),
    refreshing: request.isFetching && !request.isPending && !request.isFetchingNextPage,
    // Guarded by the model's own answer rather than by React Query's `hasNextPage`, which cannot know:
    // the end of a search is proved by a refusal or an empty page, and `state.more` reads both.
    loadMore: () => {
      if (state.kind === 'ready' && state.more && !request.isFetchingNextPage) {
        void request.fetchNextPage();
      }
    },
    loadingMore: request.isFetchingNextPage,
  };
}
